import { useState, useMemo, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
} from '@mui/material';
import { RateReview } from '@mui/icons-material';
import { enhancedApi as adminApi } from '@store/generated/admin';
import type { FeedbackSessionListItemResponse } from '@store/generated/admin';
import { AppHeader } from '../components/app-header';
import { BackButton } from '../components/back-button';
import { DataTable } from '../components/data-table';
import { FeedbackMetricCard } from '../components/feedback-metric-card';
import {
  FeedbackSessionFilters,
  type FeedbackFilters,
} from '../components/feedback-session-filters';
import { FeedbackSessionDetailDialog } from '../components/feedback-session-detail-dialog';
import { formatDateTime, formatRelativeTime } from '../utils/formatting';

const { useGetV2AdminFeedbackSessionsQuery } = adminApi;

export default function FeedbackSessionsList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);

  // Parse filters from URL
  const page = parseInt(searchParams.get('page') || '1', 10);
  const perPage = parseInt(searchParams.get('per_page') || '20', 10);
  const sessionParam = searchParams.get('session');
  const filters: FeedbackFilters = {
    userId: searchParams.get('user_id') || undefined,
    noteId: searchParams.get('note_id') || undefined,
    moduleId: searchParams.get('module_id') || undefined,
    startDate: searchParams.get('start_date') || undefined,
    endDate: searchParams.get('end_date') || undefined,
  };

  // Open detail dialog if session param is in URL
  useEffect(() => {
    if (sessionParam) {
      setSelectedSessionId(sessionParam);
    }
  }, [sessionParam]);

  // Fetch sessions data
  const {
    data: sessionsData,
    isLoading,
    error,
    refetch,
  } = useGetV2AdminFeedbackSessionsQuery({
    page,
    perPage,
    ...(filters.userId && { userId: filters.userId }),
    ...(filters.noteId && { noteId: filters.noteId }),
    ...(filters.moduleId && { moduleId: filters.moduleId }),
    ...(filters.startDate && { startDate: filters.startDate }),
    ...(filters.endDate && { endDate: filters.endDate }),
  });

  const handleFiltersChange = useCallback(
    (newFilters: FeedbackFilters) => {
      const params = new URLSearchParams();
      params.set('page', '1'); // Reset to first page on filter change

      if (newFilters.userId) params.set('user_id', newFilters.userId);
      if (newFilters.noteId) params.set('note_id', newFilters.noteId);
      if (newFilters.moduleId) params.set('module_id', newFilters.moduleId);
      if (newFilters.startDate) params.set('start_date', newFilters.startDate);
      if (newFilters.endDate) params.set('end_date', newFilters.endDate);

      setSearchParams(params);
    },
    [setSearchParams],
  );

  const handleClearFilters = useCallback(() => {
    setSearchParams(new URLSearchParams());
  }, [setSearchParams]);

  const handlePageChange = useCallback(
    (newPage: number) => {
      const params = new URLSearchParams(searchParams);
      params.set('page', newPage.toString());
      setSearchParams(params);
    },
    [searchParams, setSearchParams],
  );

  const handlePerPageChange = useCallback(
    (newPerPage: number) => {
      const params = new URLSearchParams(searchParams);
      params.set('per_page', newPerPage.toString());
      params.set('page', '1');
      setSearchParams(params);
    },
    [searchParams, setSearchParams],
  );

  const handleRowClick = useCallback((session: FeedbackSessionListItemResponse) => {
    setSelectedSessionId(session.feedback_session_id);
  }, []);

  const handleCloseDetail = useCallback(() => {
    setSelectedSessionId(null);
    // Remove session param from URL when closing
    if (sessionParam) {
      const params = new URLSearchParams(searchParams);
      params.delete('session');
      setSearchParams(params);
    }
  }, [sessionParam, searchParams, setSearchParams]);

  // Calculate metrics from sessions data
  const metrics = useMemo(() => {
    if (!sessionsData?.items) {
      return {
        totalSessions: 0,
        totalEvents: 0,
        activeProviders: 0,
        noteFeedbackCount: 0,
        sectionFeedbackCount: 0,
        avgEventsPerSession: 0,
        positiveRatings: 0,
        negativeRatings: 0,
        neutralRatings: 0,
        commentOnlyCount: 0,
        positiveRatio: 0,
        negativeRatio: 0,
        sessionsWithComments: 0,
        totalComments: 0,
      };
    }

    const sessions = sessionsData.items;
    const totalSessions = sessionsData.total || sessions.length;
    const totalEvents = sessions.reduce((sum, s) => sum + s.feedback_event_count, 0);
    const uniqueUsers = new Set(sessions.map(s => s.user_id)).size;

    const noteFeedbackCount = sessions.filter(s => s.has_note_feedback).length;
    const sectionFeedbackCount = sessions.filter(s => s.has_section_feedback).length;

    const positiveRatings = sessions.reduce((sum, s) => sum + (s.positive_rating_count || 0), 0);
    const negativeRatings = sessions.reduce((sum, s) => sum + (s.negative_rating_count || 0), 0);
    const neutralRatings = sessions.reduce((sum, s) => sum + (s.neutral_rating_count || 0), 0);
    const commentOnlyCount = sessions.filter(s => s.overall_sentiment === 'comment_only').length;

    const sessionsWithComments = sessions.filter(
      s => s.has_note_comment || (s.comment_count && s.comment_count > 0),
    ).length;
    const totalComments = sessions.reduce((sum, s) => sum + (s.comment_count || 0), 0);

    const totalRatings = positiveRatings + negativeRatings + neutralRatings;
    const positiveRatio = totalRatings > 0 ? (positiveRatings / totalRatings) * 100 : 0;
    const negativeRatio = totalRatings > 0 ? (negativeRatings / totalRatings) * 100 : 0;

    const avgEventsPerSession = totalSessions > 0 ? totalEvents / totalSessions : 0;

    return {
      totalSessions,
      totalEvents,
      activeProviders: uniqueUsers,
      noteFeedbackCount,
      sectionFeedbackCount,
      avgEventsPerSession,
      positiveRatings,
      negativeRatings,
      neutralRatings,
      commentOnlyCount,
      positiveRatio,
      negativeRatio,
      sessionsWithComments,
      totalComments,
    };
  }, [sessionsData]);

  // Helper function to get sentiment display
  const getSentimentDisplay = (session: FeedbackSessionListItemResponse) => {
    const sentiment = session.overall_sentiment;
    if (!sentiment) return null;

    const config = {
      positive: { emoji: '🟢', label: 'Positive', color: 'success' as const },
      negative: { emoji: '🔴', label: 'Negative', color: 'error' as const },
      mixed: { emoji: '🟡', label: 'Mixed', color: 'warning' as const },
      neutral: { emoji: '⚪', label: 'Neutral', color: 'default' as const },
      comment_only: { emoji: '💬', label: 'Comments', color: 'info' as const },
    };

    const { emoji, label, color } = config[sentiment];
    return (
      <Chip icon={<Box>{emoji}</Box>} label={label} color={color} size="small" variant="outlined" />
    );
  };

  // Helper function to format feedback details
  const formatFeedbackDetails = (session: FeedbackSessionListItemResponse) => {
    const parts: string[] = [];

    if (
      session.has_note_feedback &&
      session.note_rating !== null &&
      session.note_rating !== undefined
    ) {
      const emoji = session.note_rating === 0 ? '😊' : session.note_rating === 1 ? '☹️' : '😐';
      parts.push(`${emoji} Note`);
    }

    if (session.has_section_feedback && session.section_event_count) {
      parts.push(
        `${session.section_event_count} Section${session.section_event_count > 1 ? 's' : ''}`,
      );
    }

    return parts.join(' + ') || 'No feedback';
  };

  const columns = useMemo(
    () => [
      {
        key: 'user_name' as keyof FeedbackSessionListItemResponse,
        label: 'Provider',
        render: (_value: unknown, row: FeedbackSessionListItemResponse) => (
          <Box>
            <Typography variant="body2" sx={{ fontWeight: "medium" }}>
              {row.user_name || `User ${row.user_id.substring(0, 8)}...`}
            </Typography>
            {row.user_email && (
              <Typography variant="caption" color="text.secondary">
                {row.user_email}
              </Typography>
            )}
            {row.organization_name && (
              <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                {row.organization_name}
              </Typography>
            )}
          </Box>
        ),
      },
      {
        key: 'overall_sentiment' as keyof FeedbackSessionListItemResponse,
        label: 'Sentiment',
        render: (_value: unknown, row: FeedbackSessionListItemResponse) => getSentimentDisplay(row),
      },
      {
        key: 'feedback_event_count' as keyof FeedbackSessionListItemResponse,
        label: 'Details',
        render: (_value: unknown, row: FeedbackSessionListItemResponse) => (
          <Typography variant="body2" sx={{ fontSize: "0.875rem" }}>
            {formatFeedbackDetails(row)}
          </Typography>
        ),
      },
      {
        key: 'feedback_event_count' as keyof FeedbackSessionListItemResponse,
        label: 'Events',
        render: (value: unknown) => {
          const count = typeof value === 'number' ? value : 0;
          if (count === 0) {
            return <Chip label="None" size="small" color="default" variant="outlined" />;
          }
          const color = count >= 3 ? 'success' : count >= 1 ? 'warning' : 'default';
          return (
            <Chip
              label={`${count} event${count > 1 ? 's' : ''}`}
              size="small"
              color={color}
              variant="outlined"
            />
          );
        },
      },
      {
        key: 'last_modified_date' as keyof FeedbackSessionListItemResponse,
        label: 'Date',
        render: (value: unknown) => (
          <Box>
            <Typography variant="body2">{formatRelativeTime(value as string)}</Typography>
            <Typography variant="caption" color="text.secondary">
              {formatDateTime(value as string)}
            </Typography>
          </Box>
        ),
      },
    ],
    [],
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <AppHeader />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-4">
          <BackButton fallbackUrl="/admin-v2/feedback" />
        </div>

        {/* Page Header */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 4 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <RateReview fontSize="large" color="primary" />
            <Box>
              <Typography variant="h4" sx={{ fontWeight: "bold" }}>
                Feedback Sessions
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Browse all note and section feedback
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Filters */}
        <FeedbackSessionFilters
          filters={filters}
          onChange={handleFiltersChange}
          onClear={handleClearFilters}
        />

        {/* Stats Overview */}
        {sessionsData && sessionsData.items.length > 0 && (
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{ fontWeight: "medium", mb: 2 }}>
              Overview Statistics
            </Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <FeedbackMetricCard
                  title="Total Sessions"
                  value={metrics.totalSessions}
                  subtitle="In this view"
                  loading={isLoading}
                  format="number"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <FeedbackMetricCard
                  title="Total Events"
                  value={metrics.totalEvents}
                  subtitle="All feedback events"
                  loading={isLoading}
                  format="number"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <FeedbackMetricCard
                  title="Positive Ratings"
                  value={metrics.positiveRatings}
                  subtitle={`${metrics.positiveRatio.toFixed(1)}% of ratings`}
                  loading={isLoading}
                  format="number"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <FeedbackMetricCard
                  title="Negative Ratings"
                  value={metrics.negativeRatings}
                  subtitle={`${metrics.negativeRatio.toFixed(1)}% of ratings`}
                  loading={isLoading}
                  format="number"
                />
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Sessions Table */}
        {error ? (
          <Box sx={{ textAlign: "center", py: 8 }}>
            <Typography color="error" variant="h6" sx={{ mb: 2 }}>
              Failed to load sessions
            </Typography>
            <Button onClick={() => void refetch()} variant="outlined">
              Retry
            </Button>
          </Box>
        ) : (
          <>
            <DataTable<FeedbackSessionListItemResponse>
              title={`Sessions (${sessionsData?.items.length || 0} of ${sessionsData?.total || 0})`}
              columns={columns}
              data={sessionsData?.items || []}
              total={sessionsData?.total}
              loading={isLoading}
              onRowClick={handleRowClick}
              emptyMessage={
                Object.values(filters).some(v => v !== undefined)
                  ? 'No sessions match the current filters'
                  : 'No sessions found'
              }
            />

            {/* Pagination Controls */}
            {sessionsData && sessionsData.total && sessionsData.total > perPage && (
              <Box
                className="bg-white rounded-lg shadow px-6 py-4 mt-4" sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}
              >
                <Typography variant="body2" color="text.secondary">
                  Showing {Math.min((page - 1) * perPage + 1, sessionsData.total)} to{' '}
                  {Math.min(page * perPage, sessionsData.total)} of {sessionsData.total} results
                </Typography>

                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <FormControl size="small">
                    <InputLabel>Per Page</InputLabel>
                    <Select
                      value={perPage}
                      label="Per Page"
                      onChange={e => handlePerPageChange(Number(e.target.value))}
                    >
                      <MenuItem value={10}>10</MenuItem>
                      <MenuItem value={20}>20</MenuItem>
                      <MenuItem value={50}>50</MenuItem>
                      <MenuItem value={100}>100</MenuItem>
                    </Select>
                  </FormControl>

                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      disabled={page <= 1}
                      onClick={() => handlePageChange(page - 1)}
                    >
                      Previous
                    </Button>

                    <Typography variant="body2" color="text.secondary">
                      Page {page} of{' '}
                      {sessionsData.total_pages || Math.ceil(sessionsData.total / perPage)}
                    </Typography>

                    <Button
                      variant="outlined"
                      size="small"
                      disabled={
                        page >=
                        (sessionsData.total_pages || Math.ceil(sessionsData.total / perPage))
                      }
                      onClick={() => handlePageChange(page + 1)}
                    >
                      Next
                    </Button>
                  </Box>
                </Box>
              </Box>
            )}
          </>
        )}

        {/* Session Detail Dialog */}
        <FeedbackSessionDetailDialog
          open={selectedSessionId !== null}
          onClose={handleCloseDetail}
          sessionId={selectedSessionId}
        />
      </main>
    </div>
  );
}
